<template>
  <div class="mr-panel">
    <LoadingSpinner v-if="meetingAnalysisLoading" />
    <div v-else-if="meetingAnalysisError" class="alert alert-danger mb-0">{{ meetingAnalysisError }}</div>
    <div v-else-if="!meetingAnalysisRecords || meetingAnalysisRecords.length === 0" class="alert alert-secondary mb-0">
      {{ $t("lang.label.no_recordings_found_for_opportunity") }}
    </div>

    <template v-else>
      <!-- Multi-recording nav -->
      <div v-if="meetingAnalysisRecords.length > 1" class="mr-rec-nav">
        <button class="mr-rec-nav-btn" :disabled="currentRecordIndex <= 0" @click="selectMeetingAnalysisRecord(meetingAnalysisRecords[currentRecordIndex - 1])">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="10 4 6 8 10 12"/></svg>
        </button>
        <span class="mr-rec-nav-label">{{ currentRecordIndex + 1 }} / {{ meetingAnalysisRecords.length }}</span>
        <button class="mr-rec-nav-btn" :disabled="currentRecordIndex >= meetingAnalysisRecords.length - 1" @click="selectMeetingAnalysisRecord(meetingAnalysisRecords[currentRecordIndex + 1])">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 4 10 8 6 12"/></svg>
        </button>
        <span class="mr-rec-nav-date">{{ selectedMeetingAnalysisRecord ? selectedMeetingAnalysisRecord.meetingDateLabel : '' }}</span>
      </div>

      <div v-if="selectedMeetingAnalysisRecord">

        <!-- HEADER -->
        <div class="mr-modal-header">
          <div class="mr-header-top">
            <div class="mr-header-title-block">
              <span class="mr-eyebrow">{{ $t("lang.column.ai_meeting_analisis") }}</span>
              <div v-if="fromMeetingReplay">
                <h2 class="mr-title">{{ infoForReplay.business_name }}</h2>
                <p class="mr-subtitle">{{ infoForReplay.website }}</p>
              </div>
              <div v-else>
                <h2 class="mr-title">{{ opportunity && opportunity.business ? opportunity.business.name : (selectedMeetingAnalysisRecord.contactLabel || $t('lang.label.no_contact')) }}</h2>
                <p v-if="opportunity && opportunity.business && opportunity.business.url" class="mr-subtitle">{{ opportunity.business.url }}</p>
              </div>
            </div>
          </div>

          <!-- INFO BAR: grid 1fr 1fr 1fr auto with 6 items = 2 rows -->
          <div class="mr-info-bar">
            <div class="mr-info-item">
              <div class="mr-date-pill">
                <span class="mr-date-day">{{ (selectedMeetingAnalysisRecord.start_time || selectedMeetingAnalysisRecord.event_date) ? new Date(selectedMeetingAnalysisRecord.start_time || selectedMeetingAnalysisRecord.event_date).getDate() : '--' }}</span>
                <span class="mr-date-month">{{ (selectedMeetingAnalysisRecord.start_time || selectedMeetingAnalysisRecord.event_date) ? new Date(selectedMeetingAnalysisRecord.start_time || selectedMeetingAnalysisRecord.event_date).toLocaleString('default', { month: 'short' }) : '' }}</span>
              </div>
              <div class="mr-info-text">
                <div class="mr-info-label">{{ $t("lang.token.meeting_date") }}</div>
                <div class="mr-info-value">{{ selectedMeetingAnalysisRecord.meetingDateLabel }}</div>
              </div>
            </div>

            <div class="mr-info-divider"></div>

            <div class="mr-info-item">
              <div class="mr-avatar mr-avatar--sales">{{ ownerInitials(selectedMeetingAnalysisRecord) }}</div>
              <div class="mr-info-text">
                <div class="mr-info-label">{{ $t("lang.label.client_team_role_sales_rep") }}</div>
                <div v-if="fromMeetingReplay" class="mr-info-value">{{ infoForReplay.sales_rep }}</div>
                <div v-else class="mr-info-value">{{ parsePartyName(selectedMeetingAnalysisRecord.ownerLabel) }}</div>
                <div class="mr-info-value-sub">{{ $t("lang.label.owner") }}</div>
              </div>
            </div>

            <div class="mr-info-divider"></div>

            <div class="mr-info-item">
              <div class="mr-avatar mr-avatar--prospect">{{ contactInitials(selectedMeetingAnalysisRecord) }}</div>
              <div class="mr-info-text">
                <div class="mr-info-label">{{ $t("lang.label.prospect") }}</div>
                <div class="mr-info-value">{{ parsePartyName(selectedMeetingAnalysisRecord.contactLabel) }}</div>
                <div v-if="parsePartyEmail(selectedMeetingAnalysisRecord.contactLabel)" class="mr-info-value-sub">{{ parsePartyEmail(selectedMeetingAnalysisRecord.contactLabel) }}</div>
              </div>
            </div>

            <!-- <div class="mr-duration-badge">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="6" cy="6" r="5"/><polyline points="6 3 6 6 8 7"/>
              </svg>
              {{ selectedMeetingAnalysisRecord.recordingStatusLabel }}
            </div> -->
          </div>
        </div>

        <!-- TABS -->
        <div class="mr-tabs">
          <button class="mr-tab" :class="{ active: meetingAnalysisTab === 'ia' }" @click="openMeetingAnalysisTab('ia')">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 1l1.5 4.5h4.5l-3.6 2.6 1.4 4.4L8 9.8l-3.8 2.7 1.4-4.4L2 5.5h4.5z"/>
            </svg>
            {{ $t("lang.button.ia_analysis") }}
          </button>
          <button class="mr-tab" :class="{ active: meetingAnalysisTab === 'transcript' }" @click="openMeetingAnalysisTab('transcript')">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 9a2 2 0 0 1-2 2H6l-4 3V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z"/>
            </svg>
            {{ $t("lang.button.transcript") }}
          </button>
          <button class="mr-tab" :class="{ active: meetingAnalysisTab === 'media' }" @click="openMeetingAnalysisTab('media')">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="3 2 13 8 3 14 3 2"/>
            </svg>
            {{ $t("lang.button.media") }}
          </button>
          <button class="mr-tab" :class="{ active: meetingAnalysisTab === 'coaching' }" @click="openMeetingAnalysisTab('coaching')">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 14s-5-3-5-7a5 5 0 0 1 10 0c0 4-5 7-5 7z"/>
              <circle cx="8" cy="7" r="2"/>
            </svg>
            {{ $t("lang.button.coaching") }}
          </button>
          <button v-if="currentUser && currentUser.role_id == 1" class="mr-tab mr-tab--warn" :class="{ active: meetingAnalysisTab === 'retest' }" @click="openMeetingAnalysisTab('retest')">
            {{ $t("lang.button.retest") }}
          </button>
          <button v-if="currentUser && currentUser.role_id == 1" class="mr-tab mr-tab--warn" :class="{ active: meetingAnalysisTab === 'test_coaching' }" @click="openMeetingAnalysisTab('test_coaching')">
            {{ $t("lang.button.test_coaching") }}
          </button>
        </div>

        <!-- BODY -->
        <div class="mr-modal-body">

          <!-- TAB: IA Analysis -->
          <div v-if="meetingAnalysisTab === 'ia'" class="mr-tab-panel">
            <div class="mr-synthesis-grid">
              <div class="mr-summary-card">
                <div class="mr-card-header">
                  <div class="mr-card-icon mr-card-icon--primary">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M2 3h12v10H2z"/><line x1="5" y1="6" x2="11" y2="6"/><line x1="5" y1="9" x2="9" y2="9"/>
                    </svg>
                  </div>
                  <div class="mr-card-title">{{ $t("lang.label.ai_summary") }}</div>
                </div>
                <p class="mr-summary-text">{{ displayMeetingAnalysisSummary(selectedMeetingAnalysisRecord.summary) || $t("lang.label.no_summary_available") }}</p>
              </div>

              <div class="mr-score-card">
                <div class="mr-score-label">{{ $t("lang.label.meeting_ai_score") }}</div>
                <div class="mr-score-ring">
                  <svg viewBox="0 0 140 140">
                    <circle class="mr-ring-bg" cx="70" cy="70" r="60" fill="none" stroke-width="10"/>
                    <circle class="mr-ring-fg" cx="70" cy="70" r="60" fill="none" stroke-width="10"
                      stroke-dasharray="376.99"
                      :stroke-dashoffset="selectedMeetingAnalysisRecord.score != null ? (376.99 - (376.99 * selectedMeetingAnalysisRecord.score / 100)) : 376.99"
                    />
                  </svg>
                  <div class="mr-score-value">
                    <span class="mr-score-num">{{ selectedMeetingAnalysisRecord.score != null ? selectedMeetingAnalysisRecord.score : '--' }}</span>
                    <span class="mr-score-max">/ 100</span>
                  </div>
                </div>
                <div class="mr-score-footer">
                  <span class="mr-score-tag">{{ scoreLabelForScore(selectedMeetingAnalysisRecord.score) }}</span>
                </div>
              </div>
            </div>

            <div class="mr-insights-grid">
              <div class="mr-insight-card">
                <div class="mr-card-header">
                  <div class="mr-card-icon mr-card-icon--success">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 8 7 12 13 4"/></svg>
                  </div>
                  <div class="mr-card-title">{{ $t("lang.label.meeting_strengths") }}</div>
                </div>
                <ul class="mr-insight-list mr-insight-list--strengths">
                  <li v-for="(item, idx) in selectedMeetingAnalysisRecord.strengthsList" :key="'st-' + idx">{{ item }}</li>
                  <li v-if="selectedMeetingAnalysisRecord.strengthsList.length === 0">{{ $t("lang.label.meeting_no_data") }}</li>
                </ul>
              </div>

              <div class="mr-insight-card">
                <div class="mr-card-header">
                  <div class="mr-card-icon mr-card-icon--warning">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M8 1L1 14h14z"/><line x1="8" y1="6" x2="8" y2="10"/><circle cx="8" cy="12" r="0.5" fill="currentColor"/>
                    </svg>
                  </div>
                  <div class="mr-card-title">{{ $t("lang.label.meeting_weaknesses") }}</div>
                </div>
                <ul class="mr-insight-list mr-insight-list--weaknesses">
                  <li v-for="(item, idx) in selectedMeetingAnalysisRecord.weaknessesList" :key="'wk-' + idx">{{ item }}</li>
                  <li v-if="selectedMeetingAnalysisRecord.weaknessesList.length === 0">{{ $t("lang.label.meeting_no_data") }}</li>
                </ul>
              </div>

              <div class="mr-insight-card">
                <div class="mr-card-header">
                  <div class="mr-card-icon mr-card-icon--danger">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="8" cy="8" r="6"/><line x1="5" y1="5" x2="11" y2="11"/><line x1="11" y1="5" x2="5" y2="11"/>
                    </svg>
                  </div>
                  <div class="mr-card-title">{{ $t("lang.label.meeting_objections") }}</div>
                </div>
                <ul class="mr-insight-list mr-insight-list--objections">
                  <li v-for="(item, idx) in selectedMeetingAnalysisRecord.objectionsList" :key="'obj-' + idx">{{ item }}</li>
                  <li v-if="selectedMeetingAnalysisRecord.objectionsList.length === 0">{{ $t("lang.label.meeting_no_data") }}</li>
                </ul>
              </div>

              <div class="mr-insight-card">
                <div class="mr-card-header">
                  <div class="mr-card-icon mr-card-icon--primary">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="4 8 7 11 12 5"/><circle cx="8" cy="8" r="7"/>
                    </svg>
                  </div>
                  <div class="mr-card-title">{{ $t("lang.label.meeting_next_steps") }}</div>
                </div>
                <ul class="mr-insight-list mr-insight-list--steps">
                  <li v-for="(item, idx) in selectedMeetingAnalysisRecord.nextStepsList" :key="'ns-' + idx">{{ item }}</li>
                  <li v-if="selectedMeetingAnalysisRecord.nextStepsList.length === 0">{{ $t("lang.label.meeting_no_data") }}</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- TAB: Transcript -->
          <div v-else-if="meetingAnalysisTab === 'transcript'" class="mr-tab-panel">
            <div class="mr-transcript-toolbar">
              <div class="mr-search-box">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="6" cy="6" r="4"/><line x1="9" y1="9" x2="12" y2="12"/>
                </svg>
                <input v-model="transcriptSearch" type="text" :placeholder="$t('lang.label.search_transcript')">
              </div>
              <div class="mr-filter-chips">
                <button class="mr-chip" :class="{ active: transcriptFilter === 'all' }" @click="transcriptFilter = 'all'">{{ $t("lang.button.all") }}</button>
                <button class="mr-chip" :class="{ active: transcriptFilter === 'sales' }" @click="transcriptFilter = 'sales'">{{ $t("lang.label.owner") }}</button>
                <button class="mr-chip" :class="{ active: transcriptFilter === 'prospect' }" @click="transcriptFilter = 'prospect'">{{ $t("lang.label.participant") }}</button>
              </div>
            </div>
            <div v-if="filteredTranscriptLines.length === 0" class="mr-empty-state">{{ $t("lang.label.no_transcript_available") }}</div>
            <div v-else class="mr-transcript-list">
              <div v-for="(line, idx) in filteredTranscriptLines" :key="'tl-' + idx" class="mr-message">
                <div v-if="line.speaker" class="mr-message-avatar" :style="{ background: line.color }">{{ speakerInitials(line.speaker) }}</div>
                <div v-else class="mr-message-avatar mr-message-avatar--none"></div>
                <div class="mr-message-content">
                  <div v-if="line.isFirst" class="mr-message-meta">
                    <span class="mr-message-name" :style="{ color: line.color }">{{ line.speaker }}</span>
                    <span v-if="line.timestamp" class="mr-message-time">{{ line.timestamp }}</span>
                  </div>
                  <p class="mr-message-text">{{ line.message }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- TAB: Replay -->
          <div v-else-if="meetingAnalysisTab === 'media'" class="mr-tab-panel">
            <div class="mr-replay-card">
              <div class="mr-card-header">
                <div class="mr-card-icon mr-card-icon--primary">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="3 2 13 8 3 14 3 2"/>
                  </svg>
                </div>
                <div class="mr-card-title">{{ $t("lang.label.video") }}</div>
              </div>
              <div v-if="meetingMediaLoading"><LoadingSpinner /></div>
              <div v-else-if="meetingMediaError" class="alert alert-warning mb-0">{{ meetingMediaError }}</div>
              <template v-else>
                <div v-if="selectedMeetingMedia.video_urls && selectedMeetingMedia.video_urls.length > 0">
                  <div class="mr-video-frame">
                    <video controls :src="selectedMeetingMedia.video_urls[0]" style="width:100%;height:100%;object-fit:contain;"></video>
                  </div>
                  <a :href="selectedMeetingMedia.video_urls[0]" target="_blank" rel="noopener noreferrer" class="mr-btn-link mt-2 d-inline-block">{{ $t("lang.button.open_video_new_tab") }}</a>
                </div>
                <div v-else class="mr-empty-state">{{ $t("lang.label.no_video_available") }}</div>
                <div v-if="selectedMeetingMedia.audio_urls && selectedMeetingMedia.audio_urls.length > 0" class="mt-3">
                  <div class="mr-card-title mb-2">{{ $t("lang.label.audio") }}</div>
                  <audio controls :src="selectedMeetingMedia.audio_urls[0]" style="width:100%"></audio>
                </div>
                <div class="mr-replay-stats">
                  <div class="mr-stat-pill">
                    <div class="mr-stat-pill-label">{{ $t("lang.label.recording_status") }}</div>
                    <div class="mr-stat-pill-value">{{ selectedMeetingAnalysisRecord.recordingStatusLabel }}</div>
                  </div>
                  <div class="mr-stat-pill">
                    <div class="mr-stat-pill-label">{{ $t("lang.label.participant") }}</div>
                    <div class="mr-stat-pill-value" style="font-size:12px;">{{ parsePartyName(selectedMeetingAnalysisRecord.contactLabel) }}</div>
                  </div>
                  <div class="mr-stat-pill">
                    <div class="mr-stat-pill-label">{{ $t("lang.label.owner") }}</div>
                    <div class="mr-stat-pill-value" style="font-size:12px;">{{ parsePartyName(selectedMeetingAnalysisRecord.ownerLabel) }}</div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- TAB: AI Coaching -->
          <div v-else-if="meetingAnalysisTab === 'coaching'" class="mr-tab-panel">
            <div v-if="coachingConfigLoading" class="d-flex align-items-center text-muted small mb-3">
              <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              {{ $t("lang.search.loading") }}...
            </div>
            <div v-else-if="!coachingCurrentAnalysis || !coachingCurrentAnalysis.result" class="mr-empty-state">
              {{ $t("lang.label.no_summary_available") }}
            </div>
            <div v-else class="mr-coaching-card">
              <div class="mr-coaching-header">
                <div class="mr-coaching-header-left">
                  <div class="mr-coaching-header-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M10 2L12 7L17 7L13 10.5L14.5 16L10 13L5.5 16L7 10.5L3 7L8 7Z"/>
                    </svg>
                  </div>
                  <div>
                    <div class="mr-coaching-header-title">{{ $t("lang.label.personalized_sales_coaching") }}</div>
                    <div class="mr-coaching-header-sub">{{ $t("lang.label.coaching_stage_analysis_for", { name: parsePartyName(selectedMeetingAnalysisRecord.ownerLabel) }) }}</div>
                  </div>
                </div>
                <span class="mr-ai-badge">
                  <svg viewBox="0 0 11 11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:11px;height:11px;">
                    <path d="M5.5 1L7 4L10 4.5L7.5 6.5L8 9.5L5.5 8L3 9.5L3.5 6.5L1 4.5L4 4Z"/>
                  </svg>
                  {{ $t("lang.label.ai_generated") }}
                </span>
              </div>
              <div v-if="parsedCoachingData && parsedCoachingData.global_score != null" class="mr-overall-score-row">
                <span class="mr-overall-score-label">{{ $t("lang.label.overall_score") }}</span>
                <span class="mr-overall-score-pill" :class="parsedCoachingData.global_score >= 4 ? '' : parsedCoachingData.global_score >= 3 ? 'medium' : 'danger'">
                  ★ {{ parsedCoachingData.global_score }} / 5
                </span>
              </div>
              <template v-if="parsedCoachingStages && parsedCoachingStages.length">
                <div class="mr-stages-list">
                  <div v-for="(stage, si) in parsedCoachingStages" :key="'stage-' + si" class="mr-stage-card">
                    <div class="mr-stage-card-header">
                      <div class="mr-stage-name">
                        <span class="mr-stage-icon">{{ si + 1 }}</span>
                        {{ stage.name }}
                      </div>
                      <span class="mr-stage-score" :class="stage.score >= 4 ? '' : 'warning'">{{ stage.score }} / 5</span>
                    </div>
                    <div class="mr-stage-progress">
                      <div class="mr-stage-progress-fill" :class="stage.score >= 4 ? '' : 'warning'" :style="{ width: (stage.score / 5 * 100) + '%' }"></div>
                    </div>
                    <p class="mr-stage-comment">{{ stage.comment }}</p>
                  </div>
                </div>
              </template>
              <div v-if="cleanedCoachingRawText" class="mr-coaching-summary">
                <div class="mr-coaching-summary-title">{{ $t("lang.label.coaching_summary_title") }}</div>
                <p class="mr-coaching-summary-text">{{ cleanedCoachingRawText }}</p>
              </div>
            </div>
          </div>

          <!-- TAB: ReTest (admin only) -->
          <div v-else-if="meetingAnalysisTab === 'retest' && currentUser && currentUser.role_id == 1" class="mr-tab-panel">
            <div class="alert alert-warning mb-3"><strong>{{ $t("lang.button.retest") }}</strong> — {{ $t("lang.label.retest_banner") }}</div>
            <div v-if="reTestError" class="alert alert-danger mb-3">{{ reTestError }}</div>
            <div class="mb-2 small text-muted">{{ $t("lang.label.active_variable") }}: <strong>{{ reTestPromptVariableName || 'meeting_prompt_analysis' }}</strong></div>
            <div v-if="reTestConfigLoading" class="d-flex align-items-center text-muted small mb-3">
              <span class="spinner-border spinner-border-sm mr-2"></span> {{ $t("lang.search.loading") }}...
            </div>
            <div class="form-group">
              <label class="mb-1"><strong>{{ $t("lang.label.prompt_editor") }}</strong></label>
              <textarea v-model="reTestPromptDraft" class="form-control" rows="10" :disabled="reTestConfigLoading"></textarea>
              <div class="alert alert-light border mt-2 mb-0 small">
                <strong>{{ $t("lang.label.output_format_notice") }}</strong>
                <pre class="mb-0 mt-1">{"summary":"...","strengths":[],"weaknesses":[],"objections":[],"next_steps":[],"score":78}</pre>
              </div>
            </div>
            <div class="d-flex flex-wrap mb-3" style="gap:8px;">
              <button type="button" class="btn btn-sm btn-outline-primary" :disabled="reTestConfigLoading || reTestLoading || reTestApplying" @click="runReTestPrompt">{{ reTestLoading ? $t('lang.button.testing') : $t('lang.button.test_prompt') }}</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="reTestConfigLoading || reTestSavingPrompt || reTestApplying" @click="saveReTestPrompt">{{ reTestSavingPrompt ? $t('lang.button.saving') : $t('lang.button.save_prompt_replace_variable') }}</button>
              <button type="button" class="btn btn-sm btn-warning" :disabled="reTestConfigLoading || reTestApplying || reTestLoading" @click="applyReTestToAnalysis">{{ reTestApplying ? $t('lang.button.replacing_analysis') : $t('lang.button.replace_existing_analysis') }}</button>
            </div>
            <div v-if="reTestLoading" class="mb-3"><LoadingSpinner /></div>
            <div v-if="reTestResult" class="card p-3 mb-3">
              <p class="mb-1"><strong>{{ $t("lang.label.retest_result_preview") }}</strong></p>
              <p class="mb-1"><strong>{{ $t("lang.label.summary") }}:</strong> {{ displayMeetingAnalysisSummary(reTestResult.parsed && reTestResult.parsed.summary) || '-' }}</p>
              <p class="mb-1"><strong>{{ $t("lang.label.score") }}:</strong> {{ reTestResult.parsed && reTestResult.parsed.score != null ? reTestResult.parsed.score : '-' }}</p>
            </div>
            <div class="card p-3">
              <p class="mb-2"><strong>{{ $t("lang.label.prompt_version_history") }}</strong></p>
              <div v-if="!reTestHistory || reTestHistory.length === 0" class="text-muted small">{{ $t("lang.label.no_history_yet") }}</div>
              <ul v-else class="pl-3 mb-0">
                <li v-for="item in reTestHistory" :key="'hist-' + item.id" class="mb-2">
                  <div class="small text-muted">#{{ item.id }} - {{ item.created_at }}</div>
                  <div class="small"><strong>{{ $t("lang.label.prompt_colon") }}</strong> {{ (item.prompt || '').substring(0, 220) }}{{ (item.prompt || '').length > 220 ? '...' : '' }}</div>
                </li>
              </ul>
            </div>
          </div>

          <!-- TAB: Test Coaching (admin only) -->
          <div v-else-if="meetingAnalysisTab === 'test_coaching' && currentUser && currentUser.role_id == 1" class="mr-tab-panel">
            <div class="alert alert-warning mb-3"><strong>{{ $t("lang.button.coaching") }}</strong> — {{ $t("lang.label.coaching_banner") }}</div>
            <div v-if="coachingError" class="alert alert-danger mb-3">{{ coachingError }}</div>
            <div class="mb-2 small text-muted">{{ $t("lang.label.active_variable") }}: <strong>{{ coachingPromptVariableName || 'meeting_prompt_analysis' }}</strong></div>
            <div v-if="coachingConfigLoading" class="d-flex align-items-center text-muted small mb-3">
              <span class="spinner-border spinner-border-sm mr-2"></span> {{ $t("lang.search.loading") }}...
            </div>
            <div class="form-group">
              <label class="mb-1"><strong>{{ $t("lang.label.prompt_editor") }}</strong></label>
              <textarea v-model="coachingPromptDraft" class="form-control" rows="10" :disabled="coachingConfigLoading"></textarea>
            </div>
            <div class="d-flex flex-wrap mb-3" style="gap:8px;">
              <button type="button" class="btn btn-sm btn-outline-primary" :disabled="coachingConfigLoading || coachingLoading || coachingApplying" @click="runCoachingPrompt">{{ coachingLoading ? $t('lang.button.testing') : $t('lang.button.test_prompt') }}</button>
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="coachingConfigLoading || coachingSavingPrompt || coachingApplying" @click="saveCoachingPrompt">{{ coachingSavingPrompt ? $t('lang.button.saving') : $t('lang.button.save_prompt_replace_variable') }}</button>
              <button type="button" class="btn btn-sm btn-warning" :disabled="coachingConfigLoading || coachingApplying || coachingLoading" @click="applyCoachingToAnalysis">{{ coachingApplying ? $t('lang.button.replacing_analysis') : $t('lang.button.replace_existing_analysis') }}</button>
            </div>
            <div v-if="coachingLoading" class="mb-3"><LoadingSpinner /></div>
            <div v-if="coachingResult" class="card p-3 mb-3">
              <p class="mb-1"><strong>{{ $t("lang.label.coaching_result_preview") }}</strong></p>
              <div class="border rounded p-3 bg-light mt-2" style="font-size:13px;line-height:1.6;max-height:400px;overflow-y:auto;" v-html="coachingResult.raw_result || '-'"></div>
            </div>
            <div v-if="coachingCurrentAnalysis" class="card p-3 mb-3">
              <p class="mb-1"><strong>{{ $t("lang.label.current_db_analysis") }}</strong></p>
              <div v-if="coachingCurrentAnalysis.result" class="border rounded p-3 bg-light mt-2" style="font-size:13px;line-height:1.6;max-height:400px;overflow-y:auto;" v-html="coachingCurrentAnalysis.result"></div>
            </div>
            <div class="card p-3">
              <p class="mb-2"><strong>{{ $t("lang.label.prompt_version_history") }}</strong></p>
              <div v-if="!coachingHistory || coachingHistory.length === 0" class="text-muted small">{{ $t("lang.label.no_history_yet") }}</div>
              <ul v-else class="pl-3 mb-0">
                <li v-for="item in coachingHistory" :key="'co-hist-' + item.id" class="mb-2">
                  <div class="small text-muted">#{{ item.id }} - {{ item.created_at }}</div>
                  <div class="small"><strong>{{ $t("lang.label.prompt_colon") }}</strong> {{ (item.prompt || '').substring(0, 220) }}{{ (item.prompt || '').length > 220 ? '...' : '' }}</div>
                </li>
              </ul>
            </div>
          </div>

        </div><!-- /mr-modal-body -->
      </div><!-- /selectedMeetingAnalysisRecord -->
    </template><!-- /v-else -->
  </div><!-- /mr-panel -->
</template>

<script>
import { mapState } from "vuex";
import { opportunitiesService } from "@/_services";
import LoadingSpinner from "@/components/LoadingSpinner.vue";

const MEETING_ANALYSIS_EMPTY_TRANSCRIPT_SUMMARY_EN = "No transcript content was provided for analysis. The meeting context includes date, time, attendee, and client/prospect information, but the transcript itself is empty, making it impossible to evaluate the meeting dynamics, prospect interest, or outcomes.";

export default {
  name: "MeetingRecordings",

  components: { LoadingSpinner },

  props: {
    opportunityId: {
      type: Number,
      default: null,
    },
    opportunity: {
      type: Object,
      default: () => ({}),
    },
    fromMeetingReplay: {
      type: Boolean,
      default: false,
    },
    infoForReplay: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      meetingAnalysisLoading: false,
      meetingAnalysisError: "",
      meetingAnalysisRecords: [],
      meetingRecordingsLoadedForLinkId: null,
      selectedMeetingAnalysisId: null,
      requestedMeetingId: null,
      meetingAnalysisTab: "ia",
      meetingMediaLoading: false,
      meetingMediaError: "",
      meetingMediaByBotId: {},
      reTestLoading: false,
      reTestConfigLoading: false,
      reTestSavingPrompt: false,
      reTestApplying: false,
      reTestError: "",
      reTestPromptVariableName: "",
      reTestPromptDraft: "",
      reTestCurrentAnalysis: null,
      reTestHistory: [],
      reTestResult: null,
      coachingLoading: false,
      coachingConfigLoading: false,
      coachingSavingPrompt: false,
      coachingApplying: false,
      coachingError: "",
      coachingPromptVariableName: "",
      coachingPromptDraft: "",
      coachingCurrentAnalysis: null,
      coachingHistory: [],
      coachingResult: null,
      transcriptSearch: "",
      transcriptFilter: "all",
    };
  },

  computed: {
    ...mapState({
      currentUser: (state) => state.authentication.user,
    }),

    selectedMeetingAnalysisRecord() {
      if (!this.meetingAnalysisRecords || this.meetingAnalysisRecords.length === 0) {
        return null;
      }
      return (
        this.meetingAnalysisRecords.find((item) => item.meeting_recording_id === this.selectedMeetingAnalysisId) ||
        this.meetingAnalysisRecords[0]
      );
    },

    selectedMeetingMedia() {
      const record = this.selectedMeetingAnalysisRecord || {};
      const botId = record.recall_bot_id;
      const fromApi = botId ? this.meetingMediaByBotId[botId] : null;
      if (fromApi) return fromApi;
      return {
        audio_urls: record.audio_url ? [record.audio_url] : [],
        video_urls: record.video_url ? [record.video_url] : [],
      };
    },

    parsedTranscriptLines() {
      const text = (this.selectedMeetingAnalysisRecord && this.selectedMeetingAnalysisRecord.transcript_text) || "";
      if (!text.trim()) return [];
      const normalized = text.replace(/\\n/g, "\n");

      const normalizeSpeaker = (s) => s.replace(/^["""''*\s]+|["""''*\s]+$/g, "").trim();

      const palettes = {
        1: ["#0b93f6"],
        2: ["#0b93f6", "#25d366"],
        3: ["#0b93f6", "#25d366", "#e91e63"],
        4: ["#0b93f6", "#25d366", "#e91e63", "#ff9800"],
        5: ["#0b93f6", "#25d366", "#e91e63", "#ff9800", "#9c27b0"],
      };
      const fallbackPalette = ["#0b93f6", "#25d366", "#e91e63", "#ff9800", "#9c27b0", "#00bcd4"];

      const lines = normalized.split("\n");
      const speakerOrder = [];
      for (const raw of lines) {
        const line = raw.trim();
        if (!line) continue;
        const colonIdx = line.indexOf(": ");
        if (colonIdx > 0 && colonIdx < 60) {
          const speaker = normalizeSpeaker(line.substring(0, colonIdx));
          if (speaker && !speakerOrder.includes(speaker)) speakerOrder.push(speaker);
        }
      }

      const palette = palettes[speakerOrder.length] || fallbackPalette;
      const speakerColorMap = {};
      speakerOrder.forEach((sp, i) => { speakerColorMap[sp] = palette[i % palette.length]; });

      return lines.reduce((acc, raw) => {
        const line = raw.trim();
        if (!line) return acc;
        const colonIdx = line.indexOf(": ");
        if (colonIdx > 0 && colonIdx < 60) {
          const speaker = normalizeSpeaker(line.substring(0, colonIdx));
          const message = line.substring(colonIdx + 2).trim();
          acc.push({ speaker, message, color: speakerColorMap[speaker], isFirst: !acc.length || acc[acc.length - 1].speaker !== speaker });
        } else {
          acc.push({ speaker: null, message: line, color: null, isFirst: true });
        }
        return acc;
      }, []);
    },

    filteredTranscriptLines() {
      let lines = this.parsedTranscriptLines;
      if (this.transcriptSearch && this.transcriptSearch.trim()) {
        const q = this.transcriptSearch.trim().toLowerCase();
        lines = lines.filter(l => (l.message || '').toLowerCase().includes(q) || (l.speaker || '').toLowerCase().includes(q));
      }
      if (this.transcriptFilter === 'sales') {
        const speakerOrder = [...new Set(this.parsedTranscriptLines.filter(l => l.speaker).map(l => l.speaker))];
        const first = speakerOrder[0];
        lines = lines.filter(l => l.speaker === first);
      } else if (this.transcriptFilter === 'prospect') {
        const speakerOrder = [...new Set(this.parsedTranscriptLines.filter(l => l.speaker).map(l => l.speaker))];
        const second = speakerOrder[1];
        lines = lines.filter(l => l.speaker === second);
      }
      return lines;
    },

    parsedCoachingData() {
      const result = this.coachingCurrentAnalysis && this.coachingCurrentAnalysis.result;
      if (!result) return null;
      if (typeof result !== 'string') return result;

      const unwrap = (data) => Array.isArray(data) ? (data[0] || null) : data;

      // 1. Reemplazar <br> y decodificar entidades HTML
      let clean = result
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/&nbsp;/g, ' ')
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/<[^>]+>/g, '')
        .trim();

      // 2. Quitar fences markdown ```json ... ```
      clean = clean.replace(/^```[a-z]*\s*/i, '').replace(/\s*```\s*$/i, '').trim();

      // 3. Intento directo
      try { return unwrap(JSON.parse(clean)); } catch (_) {}

      // 4. Extraer el bloque { ... } más externo
      const start = clean.indexOf('{');
      const end   = clean.lastIndexOf('}');
      if (start !== -1 && end > start) {
        const slice = clean.slice(start, end + 1);

        try { return unwrap(JSON.parse(slice)); } catch (_) {}

        // 5. Reparar JSON incompleto: añadir llaves de cierre faltantes
        //    (ocurre cuando el AI trunca la respuesta y falta el } exterior)
        const opens  = (slice.match(/\{/g) || []).length;
        const closes = (slice.match(/\}/g) || []).length;
        const missing = opens - closes;
        if (missing > 0) {
          try { return unwrap(JSON.parse(slice + '}'.repeat(missing))); } catch (_) {}
        }
      }

      return null;
    },

    parsedCoachingStages() {
      const parsed = this.parsedCoachingData;
      if (!parsed) return [];
      // Nuevo formato: { global_score, opening_framing, pain_point, pitch_demo, next_step }
      if (parsed.opening_framing) {
        const stageKeys = [
          { key: 'opening_framing', i18nKey: 'lang.label.coaching_stage_opening_framing' },
          { key: 'pain_point', i18nKey: 'lang.label.coaching_stage_pain_point' },
          { key: 'pitch_demo', i18nKey: 'lang.label.coaching_stage_pitch_demo' },
          { key: 'next_step', i18nKey: 'lang.label.coaching_stage_next_step' },
        ];
        return stageKeys
          .filter(({ key }) => parsed[key])
          .map(({ key, i18nKey }) => ({
            name: this.$t(i18nKey),
            score: parsed[key].score,
            comment: parsed[key].description,
          }));
      }
      // Formato legacy con array stages
      if (Array.isArray(parsed.stages)) {
        return parsed.stages.map((stage) => ({
          ...stage,
          name: this.coachingStageDisplayName(stage.name || stage.key),
        }));
      }
      if (Array.isArray(parsed)) {
        return parsed.map((stage) => ({
          ...stage,
          name: this.coachingStageDisplayName(stage.name || stage.key),
        }));
      }
      return [];
    },

    cleanedCoachingRawText() {
      // Texto crudo del AI: usa coaching_summary del JSON si existe,
      // de lo contrario muestra el texto plano del result limpio de HTML/JSON.
      const parsed = this.parsedCoachingData;
      if (parsed && parsed.coaching_summary) {
        return this.displayMeetingAnalysisSummary(parsed.coaching_summary);
      }
      if (parsed && parsed.summary) {
        return this.displayMeetingAnalysisSummary(parsed.summary);
      }

      const raw = this.coachingCurrentAnalysis && this.coachingCurrentAnalysis.result;
      if (!raw || typeof raw !== 'string') return '';

      let clean = raw
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/<[^>]+>/g, '')
        .trim();

      // Si es JSON, no lo mostramos como texto plano
      if (clean.startsWith('{') || clean.startsWith('[')) return '';
      return clean;
    },

    currentRecordIndex() {
      if (!this.meetingAnalysisRecords || !this.selectedMeetingAnalysisRecord) return 0;
      const idx = this.meetingAnalysisRecords.findIndex(
        (r) => r.meeting_recording_id === this.selectedMeetingAnalysisId
      );
      return idx >= 0 ? idx : 0;
    },

    opportunitySubtitle() {
      if (!this.opportunity) return '';
      const parts = [];
      const biz = this.opportunity.business || {};
      const link = this.opportunity.link || {};
      if (biz.alias && biz.alias.trim()) parts.push(biz.alias.trim());
      if (!parts.length && link.website && link.website.trim()) parts.push(link.website.trim());
      if (!parts.length && link.short_description && link.short_description.trim()) {
        const s = link.short_description.trim();
        parts.push(s.length > 60 ? s.substring(0, 60) + '…' : s);
      }
      return parts.join(' · ');
    },
  },

  watch: {
    opportunityId() {
      this.meetingAnalysisRecords = [];
      this.meetingRecordingsLoadedForLinkId = null;
      this.selectedMeetingAnalysisId = null;
    },
  },

  methods: {
    // ─── Public methods (llamados desde el padre via ref) ──────────────────────

    parsePartyName(label) {
      if (!label) return '';
      const m = label.match(/^(.+?)\s*\([^)]*\)\s*$/);
      return m ? m[1].trim() : label.trim();
    },

    parsePartyEmail(label) {
      if (!label) return '';
      const m = label.match(/\(([^)]+)\)\s*$/);
      return m ? m[1].trim() : '';
    },

    ownerInitials(record) {
      const label = record.ownerLabel || '';
      const parts = label.replace(/\s*\(.*\)/, '').trim().split(' ');
      return parts.filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
    },

    contactInitials(record) {
      const label = record.contactLabel || '';
      const parts = label.replace(/\s*\(.*\)/, '').trim().split(' ');
      return parts.filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
    },

    speakerInitials(speaker) {
      if (!speaker) return '?';
      return speaker.trim().split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
    },

    scoreLabelForScore(score) {
      if (score == null) return '-';
      if (score >= 80) return this.$t('lang.label.meeting_score_strong_opportunity');
      if (score >= 60) return this.$t('lang.label.meeting_score_promising_opportunity');
      if (score >= 40) return this.$t('lang.label.meeting_score_needs_nurturing');
      return this.$t('lang.label.meeting_score_low_potential');
    },

    coachingStageDisplayName(nameOrKey) {
      const keyMap = {
        opening_framing: 'lang.label.coaching_stage_opening_framing',
        pain_point: 'lang.label.coaching_stage_pain_point',
        pitch_demo: 'lang.label.coaching_stage_pitch_demo',
        next_step: 'lang.label.coaching_stage_next_step',
        'Opening & Framing': 'lang.label.coaching_stage_opening_framing',
        'Pain Point': 'lang.label.coaching_stage_pain_point',
        'Pitch & Demo': 'lang.label.coaching_stage_pitch_demo',
        'Next Step': 'lang.label.coaching_stage_next_step',
      };
      const i18nKey = keyMap[nameOrKey];
      return i18nKey ? this.$t(i18nKey) : (nameOrKey || '');
    },

    normalizeMeetingAnalysisSummaryText(text) {
      return (text || '').trim().replace(/\s+/g, ' ');
    },

    isEmptyTranscriptAnalysisSummary(summary) {
      if (!summary || typeof summary !== 'string') return false;
      return this.normalizeMeetingAnalysisSummaryText(summary)
        === this.normalizeMeetingAnalysisSummaryText(MEETING_ANALYSIS_EMPTY_TRANSCRIPT_SUMMARY_EN);
    },

    displayMeetingAnalysisSummary(summary) {
      if (!summary) return '';
      if (this.isEmptyTranscriptAnalysisSummary(summary)) {
        return this.$t('lang.label.meeting_analysis_empty_transcript_summary');
      }
      return summary;
    },

    load(payload = {}) {
      this.requestedMeetingId = payload && payload.meetingId ? Number(payload.meetingId) : null;
      this.meetingAnalysisTab = "ia";
      this.loadMeetingRecordingsAnalysis();
    },

    async preload(force = false) {
      if (!this.opportunityId) return;
      if (!force && Number(this.meetingRecordingsLoadedForLinkId) === Number(this.opportunityId)) return;

      try {
        const result = await opportunitiesService.getMeetingRecordingsAnalysis(this.opportunityId);
        const normalized = this.normalizeMeetingAnalysisRecords(result || []);
        this.meetingAnalysisRecords = normalized;
        this.meetingRecordingsLoadedForLinkId = this.opportunityId;
        this.$emit("records-updated", { records: normalized, loadedForLinkId: this.opportunityId });
      } catch (e) {
        // Silent fail
      }
    },

    // ─── Internal methods ──────────────────────────────────────────────────────

    openMeetingAnalysisTab(tab) {
      this.meetingAnalysisTab = tab;
      if (tab === "media") this.loadMeetingMediaForSelectedRecord();
      if (tab === "retest") this.loadReTestDataForSelectedRecord();
      if (tab === "test_coaching") this.loadCoachingDataForSelectedRecord();
      if (tab === "coaching") this.loadCoachingDataForSelectedRecord();
    },

    formatCoachingResult(text) {
      if (!text) return '';
      let html = text;
      html = html.replace(/^###\s*(.+)$/gm, '<h5 class="coaching-h">$1</h5>');
      html = html.replace(/^##\s*(.+)$/gm, '<h4 class="coaching-h">$1</h4>');
      html = html.replace(/^#\s*(.+)$/gm, '<h3 class="coaching-h">$1</h3>');
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
      return html;
    },

    parseMeetingAnalysisList(value) {
      if (Array.isArray(value)) return value;
      if (!value || value === "null") return [];
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },

    formatMeetingDate(record) {
      const value = record.start_time || record.event_date || record.recording_created_at;
      if (!value) return this.$t('lang.column.meeting');
      const date = new Date(value);
      if (isNaN(date.getTime())) return String(value);
      return date.toLocaleString();
    },

    formatMeetingParty(name, email, fallbackLabel) {
      const safeName = (name || "").toString().trim();
      const safeEmail = (email || "").toString().trim();
      if (safeName && safeEmail) return `${safeName} (${safeEmail})`;
      if (safeName) return safeName;
      if (safeEmail) return safeEmail;
      return fallbackLabel;
    },

    getMeetingEventById(linkEventId) {
      const events = (this.opportunity && this.opportunity.events) ? this.opportunity.events : [];
      if (linkEventId == null) return null;
      return events.find((item) => Number(item.id) === Number(linkEventId)) || null;
    },

    getMeetingOwnerLabel(event = {}, row = {}) {
      const ownerName = [row.owner_first_name, row.owner_last_name].filter(Boolean).join(" ").trim()
        || [event.user_name, event.user_lastname].filter(Boolean).join(" ").trim();
      const ownerEmail = row.owner_email || event.user_email || event.user_mail || "";
      return this.formatMeetingParty(ownerName, ownerEmail, this.$t('lang.label.no_user'));
    },

    getMeetingContactLabel(event = {}, row = {}) {
      const contactName = event.contact_complete_name
        || event.contact_name
        || [event.first_name, event.last_name].filter(Boolean).join(" ").trim()
        || [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
      const contactEmail = event.business_contact_email
        || event.contact_email
        || event.attendee_email
        || event.email_attendee
        || row.attendee_email
        || row.email_attendee
        || "";
      return this.formatMeetingParty(contactName, contactEmail, this.$t('lang.label.no_contact'));
    },

    buildMeetingDisplayLabel(event = {}, row = {}) {
      const dateLabel = this.formatMeetingDate(row);
      const ownerLabel = this.getMeetingOwnerLabel(event, row);
      const contactLabel = this.getMeetingContactLabel(event, row);
      return `${dateLabel} ${ownerLabel} - ${contactLabel}`;
    },

    normalizeMeetingAnalysisRecords(rows) {
      const list = Array.isArray(rows) ? rows : [];
      return list.map((row) => {
        const meetingRecordingId = row.meeting_recording_id != null ? Number(row.meeting_recording_id) : null;
        const linkedEvent = this.getMeetingEventById(row.link_event_id);
        const ownerLabel = this.getMeetingOwnerLabel(linkedEvent || {}, row);
        const contactLabel = this.getMeetingContactLabel(linkedEvent || {}, row);

        return {
          ...row,
          meeting_recording_id: meetingRecordingId,
          meetingDateLabel: this.formatMeetingDate(row),
          meetingDisplayLabel: this.buildMeetingDisplayLabel(linkedEvent || {}, row),
          ownerLabel,
          contactLabel,
          recordingStatusLabel: row.recording_status || row.link_event_status || "unknown",
          strengthsList: this.parseMeetingAnalysisList(row.strengths),
          weaknessesList: this.parseMeetingAnalysisList(row.weaknesses),
          objectionsList: this.parseMeetingAnalysisList(row.objections),
          nextStepsList: this.parseMeetingAnalysisList(row.next_steps),
        };
      });
    },

    async loadMeetingMediaForSelectedRecord() {
      const record = this.selectedMeetingAnalysisRecord;
      if (!record) return;

      const botId = record.recall_bot_id;
      this.meetingMediaError = "";

      if (!botId || this.meetingMediaByBotId[botId]) return;

      this.meetingMediaLoading = true;
      try {
        const result = await opportunitiesService.getMeetingRecordingMediaByBotId(botId);
        const audioUrls = [];
        const videoUrls = [];

        if (result && Array.isArray(result.audio_urls)) {
          result.audio_urls.forEach((url) => { if (url && !audioUrls.includes(url)) audioUrls.push(url); });
        }
        if (result && result.audio_url && !audioUrls.includes(result.audio_url)) audioUrls.push(result.audio_url);
        if (record.audio_url && !audioUrls.includes(record.audio_url)) audioUrls.push(record.audio_url);

        if (result && Array.isArray(result.video_urls)) {
          result.video_urls.forEach((url) => { if (url && !videoUrls.includes(url)) videoUrls.push(url); });
        }
        if (result && result.video_url && !videoUrls.includes(result.video_url)) videoUrls.push(result.video_url);

        this.$set(this.meetingMediaByBotId, botId, {
          audio_urls: audioUrls,
          video_urls: videoUrls,
          raw: result || null,
        });
      } catch (e) {
        this.meetingMediaError = (e && (e.message || e.error)) || this.$t("lang.label.recall_media_error");
      } finally {
        this.meetingMediaLoading = false;
      }
    },

    async loadReTestDataForSelectedRecord() {
      const record = this.selectedMeetingAnalysisRecord;
      if (!record || !record.meeting_recording_id) return;

      this.reTestConfigLoading = true;
      this.reTestError = "";
      this.reTestResult = null;
      this.reTestCurrentAnalysis = null;
      this.reTestHistory = [];

      try {
        const result = await opportunitiesService.getMeetingRetestData(record.meeting_recording_id);
        this.reTestPromptVariableName = (result && result.prompt_variable_name) || "meeting_prompt_analysis";
        this.reTestPromptDraft = (result && result.current_prompt) || "";
        this.reTestCurrentAnalysis = (result && result.current_analysis) || null;
        this.reTestHistory = (result && Array.isArray(result.history)) ? result.history : [];
      } catch (e) {
        this.reTestError = (e && (e.message || e.error)) || this.$t("lang.label.retest_load_config_error");
      } finally {
        this.reTestConfigLoading = false;
      }
    },

    async runReTestPrompt() {
      const record = this.selectedMeetingAnalysisRecord;
      if (!record || !record.meeting_recording_id) return;

      if (!this.reTestPromptDraft || !this.reTestPromptDraft.trim()) {
        this.$swal.fire("", this.$t("lang.label.prompt_empty_error"), "warning");
        return;
      }

      this.reTestLoading = true;
      this.reTestError = "";
      this.reTestResult = null;

      try {
        const result = await opportunitiesService.testMeetingRetestPrompt(record.meeting_recording_id, this.reTestPromptDraft);
        this.reTestResult = result || null;
      } catch (e) {
        this.reTestError = (e && (e.message || e.error)) || this.$t("lang.label.retest_run_test_error");
      } finally {
        this.reTestLoading = false;
      }
    },

    async saveReTestPrompt() {
      const record = this.selectedMeetingAnalysisRecord;
      if (!record || !record.meeting_recording_id) return;

      if (!this.reTestPromptDraft || !this.reTestPromptDraft.trim()) {
        this.$swal.fire("", this.$t("lang.label.prompt_empty_error"), "warning");
        return;
      }

      this.reTestSavingPrompt = true;
      this.reTestError = "";

      try {
        await opportunitiesService.saveMeetingRetestPrompt(record.meeting_recording_id, this.reTestPromptDraft);
        this.$swal.fire("", this.$t("lang.label.retest_prompt_saved_success"), "success");
        await this.loadReTestDataForSelectedRecord();
      } catch (e) {
        this.reTestError = (e && (e.message || e.error)) || this.$t("lang.label.retest_save_prompt_error");
      } finally {
        this.reTestSavingPrompt = false;
      }
    },

    async applyReTestToAnalysis() {
      const record = this.selectedMeetingAnalysisRecord;
      if (!record || !record.meeting_recording_id) return;

      if (!this.reTestPromptDraft || !this.reTestPromptDraft.trim()) {
        this.$swal.fire("", this.$t("lang.label.prompt_empty_error"), "warning");
        return;
      }

      if (!this.reTestResult || !this.reTestResult.parsed) {
        this.$swal.fire("", this.$t("lang.label.retest_run_test_first_error"), "warning");
        return;
      }

      const confirm = await this.$swal.fire({
        title: this.$t("lang.label.confirm_replace_existing_analysis_title"),
        text: this.$t("lang.label.confirm_replace_existing_analysis_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("lang.button.confirm_replace"),
        cancelButtonText: this.$t("lang.button.cancel"),
      });

      if (!confirm || (!confirm.isConfirmed && confirm.value !== true)) return;

      this.reTestApplying = true;
      this.reTestError = "";

      try {
        const applied = await opportunitiesService.applyMeetingRetestAnalysis(
          record.meeting_recording_id,
          this.reTestPromptDraft,
          this.reTestResult.parsed,
          this.reTestResult.raw_result || null
        );

        const updated = {
          ...record,
          summary: applied.summary || null,
          strengths: applied.strengths || "[]",
          weaknesses: applied.weaknesses || "[]",
          objections: applied.objections || "[]",
          next_steps: applied.next_steps || "[]",
          score: applied.score,
          ai_result: applied.result || null,
          strengthsList: this.parseMeetingAnalysisList(applied.strengths || "[]"),
          weaknessesList: this.parseMeetingAnalysisList(applied.weaknesses || "[]"),
          objectionsList: this.parseMeetingAnalysisList(applied.objections || "[]"),
          nextStepsList: this.parseMeetingAnalysisList(applied.next_steps || "[]"),
        };

        this.meetingAnalysisRecords = this.meetingAnalysisRecords.map((item) =>
          Number(item.meeting_recording_id) === Number(record.meeting_recording_id) ? updated : item
        );

        this.reTestCurrentAnalysis = {
          ...(this.reTestCurrentAnalysis || {}),
          summary: applied.summary || null,
          strengths: applied.strengths || "[]",
          weaknesses: applied.weaknesses || "[]",
          objections: applied.objections || "[]",
          next_steps: applied.next_steps || "[]",
          score: applied.score,
          result: applied.result || null,
        };

        this.$swal.fire("", this.$t("lang.label.retest_replace_existing_analysis_success"), "success");
        await this.loadReTestDataForSelectedRecord();
      } catch (e) {
        this.reTestError = (e && (e.message || e.error)) || this.$t("lang.label.retest_replace_existing_analysis_error");
      } finally {
        this.reTestApplying = false;
      }
    },

    async loadCoachingDataForSelectedRecord() {
      const record = this.selectedMeetingAnalysisRecord;
      if (!record || !record.meeting_recording_id) return;

      this.coachingConfigLoading = true;
      this.coachingError = "";
      this.coachingResult = null;
      this.coachingCurrentAnalysis = null;
      this.coachingHistory = [];

      try {
        const result = await opportunitiesService.getMeetingCoachingData(record.meeting_recording_id);
        this.coachingPromptVariableName = (result && result.prompt_variable_name) || "meeting_prompt_analysis";
        this.coachingPromptDraft = (result && result.current_prompt) || "";
        this.coachingCurrentAnalysis = (result && result.current_analysis) || null;
        this.coachingHistory = (result && Array.isArray(result.history)) ? result.history : [];
      } catch (e) {
        this.coachingError = (e && (e.message || e.error)) || this.$t("lang.label.coaching_load_config_error");
      } finally {
        this.coachingConfigLoading = false;
      }
    },

    async runCoachingPrompt() {
      const record = this.selectedMeetingAnalysisRecord;
      if (!record || !record.meeting_recording_id) return;

      if (!this.coachingPromptDraft || !this.coachingPromptDraft.trim()) {
        this.$swal.fire("", this.$t("lang.label.prompt_empty_error"), "warning");
        return;
      }

      this.coachingLoading = true;
      this.coachingError = "";
      this.coachingResult = null;

      try {
        const result = await opportunitiesService.testMeetingCoachingPrompt(record.meeting_recording_id, this.coachingPromptDraft);
        this.coachingResult = result || null;
      } catch (e) {
        this.coachingError = (e && (e.message || e.error)) || this.$t("lang.label.coaching_run_test_error");
      } finally {
        this.coachingLoading = false;
      }
    },

    async saveCoachingPrompt() {
      const record = this.selectedMeetingAnalysisRecord;
      if (!record || !record.meeting_recording_id) return;

      if (!this.coachingPromptDraft || !this.coachingPromptDraft.trim()) {
        this.$swal.fire("", this.$t("lang.label.prompt_empty_error"), "warning");
        return;
      }

      this.coachingSavingPrompt = true;
      this.coachingError = "";

      try {
        await opportunitiesService.saveMeetingCoachingPrompt(record.meeting_recording_id, this.coachingPromptDraft);
        this.$swal.fire("", this.$t("lang.label.coaching_prompt_saved_success"), "success");
        await this.loadCoachingDataForSelectedRecord();
      } catch (e) {
        this.coachingError = (e && (e.message || e.error)) || this.$t("lang.label.coaching_save_prompt_error");
      } finally {
        this.coachingSavingPrompt = false;
      }
    },

    async applyCoachingToAnalysis() {
      const record = this.selectedMeetingAnalysisRecord;
      if (!record || !record.meeting_recording_id) return;

      if (!this.coachingPromptDraft || !this.coachingPromptDraft.trim()) {
        this.$swal.fire("", this.$t("lang.label.prompt_empty_error"), "warning");
        return;
      }

      if (!this.coachingResult || !this.coachingResult.parsed) {
        this.$swal.fire("", this.$t("lang.label.coaching_run_test_first_error"), "warning");
        return;
      }

      const confirm = await this.$swal.fire({
        title: this.$t("lang.label.confirm_replace_existing_analysis_title"),
        text: this.$t("lang.label.confirm_replace_existing_analysis_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("lang.button.confirm_replace"),
        cancelButtonText: this.$t("lang.button.cancel"),
      });

      if (!confirm || (!confirm.isConfirmed && confirm.value !== true)) return;

      this.coachingApplying = true;
      this.coachingError = "";

      try {
        const applied = await opportunitiesService.applyMeetingCoachingAnalysis(
          record.meeting_recording_id,
          this.coachingPromptDraft,
          this.coachingResult.parsed,
          this.coachingResult.raw_result || null
        );

        const updated = {
          ...record,
          summary: applied.summary || null,
          strengths: applied.strengths || "[]",
          weaknesses: applied.weaknesses || "[]",
          objections: applied.objections || "[]",
          next_steps: applied.next_steps || "[]",
          score: applied.score,
          ai_result: applied.result || null,
          strengthsList: this.parseMeetingAnalysisList(applied.strengths || "[]"),
          weaknessesList: this.parseMeetingAnalysisList(applied.weaknesses || "[]"),
          objectionsList: this.parseMeetingAnalysisList(applied.objections || "[]"),
          nextStepsList: this.parseMeetingAnalysisList(applied.next_steps || "[]"),
        };

        this.meetingAnalysisRecords = this.meetingAnalysisRecords.map((item) =>
          Number(item.meeting_recording_id) === Number(record.meeting_recording_id) ? updated : item
        );

        this.coachingCurrentAnalysis = {
          ...(this.coachingCurrentAnalysis || {}),
          summary: applied.summary || null,
          strengths: applied.strengths || "[]",
          weaknesses: applied.weaknesses || "[]",
          objections: applied.objections || "[]",
          next_steps: applied.next_steps || "[]",
          score: applied.score,
          result: applied.result || null,
        };

        this.$swal.fire("", this.$t("lang.label.coaching_replace_existing_analysis_success"), "success");
        await this.loadCoachingDataForSelectedRecord();
      } catch (e) {
        this.coachingError = (e && (e.message || e.error)) || this.$t("lang.label.coaching_replace_existing_analysis_error");
      } finally {
        this.coachingApplying = false;
      }
    },

    selectMeetingAnalysisRecord(record) {
      this.selectedMeetingAnalysisId = record ? record.meeting_recording_id : null;
      this.meetingAnalysisTab = "ia";
      this.reTestError = "";
      this.reTestResult = null;
      this.reTestCurrentAnalysis = null;
      this.reTestHistory = [];
      this.coachingError = "";
      this.coachingResult = null;
      this.coachingCurrentAnalysis = null;
      this.coachingHistory = [];
    },

    async loadMeetingRecordingsAnalysis() {
      this.meetingAnalysisLoading = true;
      this.meetingAnalysisError = "";
      this.meetingAnalysisRecords = [];
      this.selectedMeetingAnalysisId = null;
      this.meetingMediaByBotId = {};
      this.meetingMediaLoading = false;
      this.meetingMediaError = "";
      this.reTestLoading = false;
      this.reTestConfigLoading = false;
      this.reTestSavingPrompt = false;
      this.reTestApplying = false;
      this.reTestError = "";
      this.reTestPromptVariableName = "";
      this.reTestPromptDraft = "";
      this.reTestCurrentAnalysis = null;
      this.reTestHistory = [];
      this.reTestResult = null;

      try {
        const result = await opportunitiesService.getMeetingRecordingsAnalysis(this.opportunityId);
        const normalized = this.normalizeMeetingAnalysisRecords(result || []);
        this.meetingAnalysisRecords = normalized;
        this.meetingRecordingsLoadedForLinkId = this.opportunityId;
        this.$emit("records-updated", { records: normalized, loadedForLinkId: this.opportunityId });

        if (normalized.length > 0) {
          const requestedId = this.requestedMeetingId;
          const requestedRecord = requestedId != null
            ? normalized.find((item) => Number(item.link_event_id) === requestedId || Number(item.meeting_recording_id) === requestedId)
            : null;
          this.selectedMeetingAnalysisId = requestedRecord
            ? requestedRecord.meeting_recording_id
            : normalized[0].meeting_recording_id;
        }
      } catch (e) {
        this.meetingAnalysisError = (e && (e.message || e.error)) || this.$t("lang.label.meeting_analysis_load_error");
      } finally {
        this.meetingAnalysisLoading = false;
      }
    },
  },
};
</script>

<style>
/* =============================================================
   MeetingRecordings – port exact du mockup modal_analyse_ia.html
   Toutes les CSS vars remplacées par valeurs codées en dur.
   Préfixe mr- sur tous les sélecteurs pour éviter collisions.
   Style global (pas scoped) – survit au téléport vue-js-modal.
============================================================= */

.mr-panel {
  font-family: 'Plus Jakarta Sans', 'Inter', 'Segoe UI', system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  background: #FFFFFF;
  color: #14141F;
}

/* Recording nav */
.mr-rec-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: #F7F7FB;
  border-bottom: 1px solid #ECECF3;
  font-size: 12px;
  font-weight: 600;
  color: #14141F;
}
.mr-rec-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #ECECF3;
  border-radius: 6px;
  background: #FFFFFF;
  cursor: pointer;
  color: #14141F;
  padding: 0;
  transition: background 0.12s;
}
.mr-rec-nav-btn:hover:not(:disabled) { background: #EEEFFE; }
.mr-rec-nav-btn:disabled { opacity: 0.35; cursor: default; }
.mr-rec-nav-label { font-weight: 700; }
.mr-rec-nav-date { color: #6B6B80; font-weight: 500; margin-left: 4px; }

/* ── HEADER ── */
.mr-modal-header {
  padding: 24px 32px;
  border-bottom: 1px solid #ECECF3;
  background: #FFFFFF;
}

.mr-header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 20px;
}

.mr-header-title-block { flex: 1; min-width: 0; }

.mr-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5B5FE9;
  background: #EEEFFE;
  padding: 5px 10px;
  border-radius: 999px;
  margin-bottom: 10px;
}
.mr-eyebrow::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #5B5FE9;
  box-shadow: 0 0 0 3px rgba(91,95,233,0.2);
}

.mr-title {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #14141F;
  line-height: 1.2;
  margin: 0 0 4px;
}

.mr-subtitle {
  font-size: 13px;
  color: #6B6B80;
  font-weight: 500;
  margin: 0;
}

/* ── INFO BAR ── */
.mr-info-bar {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 14px 18px;
  background: #F7F7FB;
  border-radius: 12px;
  border: 1px solid #ECECF3;
}

.mr-info-item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.mr-info-divider {
  width: 1px;
  height: 32px;
  background: #ECECF3;
}

.mr-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
  color: white;
  flex-shrink: 0;
}
.mr-avatar--sales    { background: linear-gradient(135deg, #5B5FE9, #7B7FF5); }
.mr-avatar--prospect { background: linear-gradient(135deg, #10B981, #34D399); }

.mr-info-text { min-width: 0; flex: 1; }

.mr-info-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9999AB;
  margin-bottom: 2px;
}

.mr-info-value {
  font-size: 13px;
  font-weight: 600;
  color: #14141F;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mr-info-value-sub {
  font-size: 11px;
  color: #6B6B80;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mr-date-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #ECECF3;
  border-radius: 10px;
  padding: 6px 12px;
  min-width: 56px;
  flex-shrink: 0;
}
.mr-date-day   { font-size: 18px; font-weight: 700; color: #14141F; line-height: 1; }
.mr-date-month { font-size: 10px; font-weight: 600; color: #6B6B80; text-transform: uppercase; letter-spacing: 0.06em; margin-top: 2px; }

.mr-duration-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 10px;
  background: #FFFFFF;
  border: 1px solid #ECECF3;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #14141F;
  flex-shrink: 0;
}
.mr-duration-badge svg { color: #5B5FE9; }

/* ── TABS ── */
.mr-tabs {
  display: flex;
  gap: 4px;
  padding: 0 32px;
  border-bottom: 1px solid #ECECF3;
  background: #FFFFFF;
  overflow-x: auto;
  scrollbar-width: none;
}
.mr-tabs::-webkit-scrollbar { display: none; }

.mr-tab {
  padding: 14px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #6B6B80;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: -1px;
  transition: all 0.15s ease;
  font-family: inherit;
  white-space: nowrap;
}
.mr-tab svg { width: 16px; height: 16px; }
.mr-tab:hover { color: #14141F; }
.mr-tab.active { color: #5B5FE9; border-bottom-color: #5B5FE9; }
.mr-tab--warn { color: #F59E0B; }
.mr-tab--warn.active { color: #F59E0B; border-bottom-color: #F59E0B; }

/* ── BODY ── */
.mr-modal-body {
  overflow-y: auto;
  padding: 28px 32px;
  background: #F7F7FB;
  min-height: 400px;
}
.mr-modal-body::-webkit-scrollbar { width: 8px; }
.mr-modal-body::-webkit-scrollbar-track { background: transparent; }
.mr-modal-body::-webkit-scrollbar-thumb { background: #DDDDE8; border-radius: 4px; }

.mr-tab-panel { animation: mrFadeIn 0.25s ease; }

@keyframes mrFadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── SUMMARY + SCORE ── */
.mr-synthesis-grid {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 20px;
  margin-bottom: 20px;
}

.mr-summary-card {
  background: #FFFFFF;
  border: 1px solid #ECECF3;
  border-radius: 16px;
  padding: 22px;
}

.mr-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.mr-card-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.mr-card-icon--primary { background: #EEEFFE; color: #5B5FE9; }
.mr-card-icon--success { background: #ECFDF5; color: #10B981; }
.mr-card-icon--warning { background: #FFFBEB; color: #F59E0B; }
.mr-card-icon--danger  { background: #FEF2F2; color: #EF4444; }

.mr-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #14141F;
  letter-spacing: -0.01em;
}

.mr-summary-text {
  font-size: 14px;
  line-height: 1.65;
  color: #14141F;
}

.mr-score-card {
  background: linear-gradient(160deg, #5B5FE9 0%, #7B5FE9 100%);
  border-radius: 16px;
  padding: 22px;
  color: white;
  position: relative;
  overflow: hidden;
  text-align: center;
}
.mr-score-card::before {
  content: '';
  position: absolute;
  top: -40px; right: -40px;
  width: 160px; height: 160px;
  background: radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%);
  border-radius: 50%;
}

.mr-score-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.85;
  position: relative;
}

.mr-score-ring {
  width: 140px;
  height: 140px;
  margin: 14px auto 8px;
  position: relative;
}
.mr-score-ring svg { transform: rotate(-90deg); width: 100%; height: 100%; }
.mr-ring-bg { stroke: rgba(255,255,255,0.18); }
.mr-ring-fg { stroke: white; stroke-linecap: round; transition: stroke-dashoffset 1s ease; }

.mr-score-value {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.mr-score-num { font-size: 38px; font-weight: 700; line-height: 1; letter-spacing: -0.03em; }
.mr-score-max { font-size: 12px; opacity: 0.7; margin-top: 2px; }

.mr-score-footer { text-align: center; margin-top: 4px; }
.mr-score-tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  background: rgba(255,255,255,0.2);
  padding: 5px 10px;
  border-radius: 999px;
  backdrop-filter: blur(8px);
  position: relative;
}

/* ── INSIGHTS ── */
.mr-insights-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.mr-insight-card {
  background: #FFFFFF;
  border: 1px solid #ECECF3;
  border-radius: 16px;
  padding: 20px;
}

.mr-insight-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mr-insight-list li {
  font-size: 13px;
  line-height: 1.5;
  color: #14141F;
  padding-left: 22px;
  position: relative;
}
.mr-insight-list li::before {
  content: '';
  position: absolute;
  left: 0; top: 6px;
  width: 14px; height: 14px;
  border-radius: 50%;
  background-size: 14px;
}
.mr-insight-list--strengths li::before {
  background: #ECFDF5 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14' fill='none' stroke='%2310B981' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='3 7 6 10 11 4'/%3E%3C/svg%3E") center/14px no-repeat;
}
.mr-insight-list--weaknesses li::before {
  background: #FFFBEB url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14' fill='none' stroke='%23F59E0B' stroke-width='2.5' stroke-linecap='round'%3E%3Cline x1='3' y1='7' x2='11' y2='7'/%3E%3C/svg%3E") center/14px no-repeat;
}
.mr-insight-list--objections li::before {
  background: #FEF2F2 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14' fill='none' stroke='%23EF4444' stroke-width='2.5' stroke-linecap='round'%3E%3Cline x1='4' y1='4' x2='10' y2='10'/%3E%3Cline x1='10' y1='4' x2='4' y2='10'/%3E%3C/svg%3E") center/14px no-repeat;
}
.mr-insight-list--steps li::before {
  background: #EEEFFE url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14' fill='none' stroke='%235B5FE9' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='5 3 9 7 5 11'/%3E%3C/svg%3E") center/14px no-repeat;
}

/* ── TRANSCRIPT ── */
.mr-transcript-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  gap: 12px;
}

.mr-search-box {
  flex: 1;
  max-width: 320px;
  position: relative;
}
.mr-search-box input {
  width: 100%;
  padding: 10px 14px 10px 38px;
  border: 1px solid #ECECF3;
  border-radius: 10px;
  font-size: 13px;
  font-family: inherit;
  background: #FFFFFF;
  color: #14141F;
  outline: none;
  transition: all 0.15s ease;
}
.mr-search-box input:focus { border-color: #5B5FE9; box-shadow: 0 0 0 3px rgba(91,95,233,0.1); }
.mr-search-box svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9999AB; }

.mr-filter-chips { display: flex; gap: 6px; }

.mr-chip {
  padding: 7px 12px;
  background: #FFFFFF;
  border: 1px solid #ECECF3;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: #6B6B80;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}
.mr-chip:hover { color: #14141F; }
.mr-chip.active { background: #14141F; color: white; border-color: #14141F; }

.mr-transcript-list {
  background: #FFFFFF;
  border: 1px solid #ECECF3;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.mr-message { display: flex; gap: 12px; align-items: flex-start; }

.mr-message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: white;
  background: #9999AB;
}
.mr-message-avatar--none { background: transparent; }

.mr-message-content { flex: 1; min-width: 0; }
.mr-message-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.mr-message-name { font-size: 12px; font-weight: 700; color: #14141F; }
.mr-message-time { font-size: 11px; color: #9999AB; font-family: monospace; }
.mr-message-text { font-size: 13.5px; line-height: 1.55; color: #14141F; margin: 0; }

/* ── REPLAY ── */
.mr-replay-card {
  background: #FFFFFF;
  border: 1px solid #ECECF3;
  border-radius: 16px;
  padding: 22px;
}

.mr-video-frame {
  width: 100%;
  aspect-ratio: 16/9;
  background: #1a1a2e;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
}

.mr-replay-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 18px;
}
.mr-stat-pill { background: #F7F7FB; border: 1px solid #ECECF3; border-radius: 10px; padding: 12px 14px; }
.mr-stat-pill-label { font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #9999AB; margin-bottom: 4px; }
.mr-stat-pill-value { font-size: 16px; font-weight: 700; color: #14141F; }

/* ── COACHING ── */
.mr-coaching-card {
  background: #FFFFFF;
  border: 1px solid #ECECF3;
  border-radius: 16px;
  padding: 26px 28px;
}

.mr-coaching-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid #ECECF3;
}

.mr-coaching-header-left { display: flex; align-items: center; gap: 12px; }

.mr-coaching-header-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #5B5FE9, #7B5FE9);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.mr-coaching-header-title { font-size: 16px; font-weight: 700; color: #14141F; letter-spacing: -0.01em; }
.mr-coaching-header-sub { font-size: 12px; color: #6B6B80; margin-top: 2px; }

.mr-ai-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: #EEEFFE;
  color: #5B5FE9;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.mr-overall-score-row { display: flex; align-items: center; gap: 14px; margin-bottom: 22px; }
.mr-overall-score-label { font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #9999AB; }

.mr-overall-score-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  background: #ECFDF5;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  color: #047857;
}
.mr-overall-score-pill.medium { background: #FFFBEB; color: #B45309; }
.mr-overall-score-pill.danger  { background: #FEF2F2; color: #B91C1C; }

.mr-stages-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 22px; }

.mr-stage-card {
  background: #F7F7FB;
  border: 1px solid #ECECF3;
  border-radius: 12px;
  padding: 16px 18px;
  transition: all 0.2s ease;
}
.mr-stage-card:hover { border-color: #DDDDE8; background: #FFFFFF; }

.mr-stage-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; gap: 12px; }

.mr-stage-name { font-size: 14px; font-weight: 700; color: #14141F; display: flex; align-items: center; gap: 8px; }

.mr-stage-icon {
  width: 22px; height: 22px;
  border-radius: 6px;
  background: #EEEFFE;
  color: #5B5FE9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.mr-stage-score { font-size: 13px; font-weight: 700; color: #059669; flex-shrink: 0; }
.mr-stage-score.warning { color: #D97706; }
.mr-stage-score.danger  { color: #DC2626; }

.mr-stage-progress { height: 6px; background: rgba(16,185,129,0.12); border-radius: 999px; overflow: hidden; margin-bottom: 12px; }
.mr-stage-progress-fill { height: 100%; background: linear-gradient(90deg, #10B981, #34D399); border-radius: 999px; transition: width 0.6s ease; }
.mr-stage-progress-fill.warning { background: linear-gradient(90deg, #F59E0B, #FBBF24); }
.mr-stage-progress-fill.danger  { background: linear-gradient(90deg, #EF4444, #F87171); }

.mr-stage-comment { font-size: 13px; line-height: 1.55; color: #14141F; margin: 0; }

.mr-coaching-summary { margin-top: 22px; padding-top: 22px; border-top: 1px solid #ECECF3; }
.mr-coaching-summary-title { font-size: 14px; font-weight: 700; color: #14141F; margin-bottom: 10px; }
.mr-coaching-summary-text { font-size: 13.5px; line-height: 1.65; color: #14141F; }

/* ── MISC ── */
.mr-empty-state {
  padding: 40px;
  text-align: center;
  color: #9999AB;
  font-size: 14px;
  background: #FFFFFF;
  border: 1px solid #ECECF3;
  border-radius: 16px;
}

.mr-btn-link { font-size: 13px; color: #5B5FE9; text-decoration: none; font-weight: 600; }
.mr-btn-link:hover { text-decoration: underline; }

@media (max-width: 768px) {
  .mr-info-bar { grid-template-columns: 1fr; }
  .mr-info-divider { display: none; }
  .mr-synthesis-grid { grid-template-columns: 1fr; }
  .mr-insights-grid { grid-template-columns: 1fr; }
  .mr-modal-header, .mr-modal-body { padding-left: 16px; padding-right: 16px; }
  .mr-tabs { padding: 0 16px; }
}
</style>